import React from 'react';

/**
 * Bottom-of-page save bar. Left side: pending count or "All changes
 * saved". Right side: Reset (ghost) + Save Changes (primary).
 *
 * Pinned inside the scroll region (not viewport-fixed) per handoff.
 *
 * Props:
 *   pendingCount  — number of dirty fields. 0 → "All changes saved"
 *   saving        — boolean; Save button shows spinner
 *   onReset       — handler
 *   onSave        — handler
 */
export default function SaveBar({ pendingCount = 0, saving = false, onReset, onSave }) {
  const hasPending = pendingCount > 0;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        marginTop: 16,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
        {hasPending && (
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />
        )}
        <span style={{
          fontSize: 14,
          fontWeight: hasPending ? 500 : 400,
          color: hasPending ? 'var(--text-2)' : 'var(--text-3)',
        }}>
          {hasPending
            ? `${pendingCount} unsaved change${pendingCount === 1 ? '' : 's'}`
            : 'All changes saved'}
        </span>
      </div>
      <button
        type="button"
        onClick={onReset}
        disabled={!hasPending || saving}
        style={{
          height: 32, padding: '0 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 8,
          color: 'var(--text-2)',
          fontSize: 14.5, fontWeight: 500,
          cursor: !hasPending || saving ? 'not-allowed' : 'pointer',
          opacity: !hasPending || saving ? 0.5 : 1,
        }}
      >
        Reset
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={!hasPending || saving}
        style={{
          height: 32, padding: '0 14px',
          background: 'var(--accent)',
          border: 'none',
          borderRadius: 8,
          color: '#fff',
          fontSize: 14.5, fontWeight: 500,
          cursor: !hasPending || saving ? 'not-allowed' : 'pointer',
          opacity: !hasPending || saving ? 0.6 : 1,
          boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 40%, black), 0 1px 2px oklch(0.3 0.05 135 / 0.18)',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        {saving ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite' }}>
              <path d="M21 12a9 9 0 1 1-6.2-8.55"/>
            </svg>
            Saving…
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m5 12 5 5L20 7"/>
            </svg>
            Save Changes
          </>
        )}
      </button>
    </div>
  );
}
